import { ErrorResponse } from 'react-router-dom';
import { IEnum } from '../services/type';
import { IUseHttpServiceProps } from '../services/type';
import { RequestMethods } from '../services/enum';

export default class EnumService {
  private readonly baseUrl = 'enums/enums/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
  ) {}

  async fetchCurrentAccounts(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-current-account/`,
    });
  }

  async fetchMotorInsuranceBranches(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-motor-insurance-branches/`,
    });
  }

  async fetchMotorStatusChoices(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-motor-status-choices/`,
    });
  }

  async fetchAbroadInsuranceBranches(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-abroad-insurance-branches/`,
    });
  }

  async fetchCountries(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-countries/`,
    });
  }

  async fetchPoliciesStatusChoices(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-policies-status-choices/`,
    });
  }

  async fetchGroupHealthInsurancePoliciesStatusChoices(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-group-health-insurance-policies-status-choices/`,
    });
  }

  async fetchDomesticInsuranceBranches(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-domestic-insurance-branches/`,
    });
  }

  async fetchDomesticInsuranceTypes(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-doctype/`,
    });
  }

  async fetchDomesticStatusChoices(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-domestic-status-choices/`,
    });
  }

  async fetchOtherInsuranceBranches(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-other-insurance-branches/`,
    });
  }

  async fetchGroupHealthInsurancePolicyTypes(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-group-health-insurance-policy-types/`,
    });
  }

  async fetchPolicyNumberAndType(
    organizationId: number,
  ): Promise<
    { id: number; policy_number_and_type: string }[] | ErrorResponse | null
  > {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `health-insurances/group-health-insurance-policies/${organizationId}/get-policy-number-and-type/`,
    });
  }

  async fetchAllCountries(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-all-countries/`,
    });
  }

  async fetchTravelHealthPolicyTypes(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-travel-health-insurance-policy-types`,
    });
  }

  async fetchUnitOfGoods(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-unit-of-goods/`,
    });
  }

  async fetchVehicleTypes(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-vehicle-types/`,
    });
  }

  async fetchCurrencies(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-currencies/`,
    });
  }

  async fetchExpeditionCertificatesTransportationTypes(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-expedition-certificates-transportation-types/`,
    });
  }

  async fetchPolicyTypes(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-policy-types/`,
    });
  }

  async fetchPolicyModels(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-policy-models/`,
    });
  }

  async fetchDamageManagementBranches(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-damage-management-branches/`,
    });
  }

  async fetchCities(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-cities/`,
    });
  }

  async fetchDamageManagementDamageTypes(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-damage-management-damage-types/`,
    });
  }

  async fetchDamageManagementStatusChoices(): Promise<
    IEnum[] | ErrorResponse | null
  > {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-damage-management-status-choices/`,
    });
  }

  async fetchDependentPayments(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-dependent-payments/`,
    });
  }

  async fetchBranchChoices(): Promise<IEnum[] | ErrorResponse | null> {
    return await this.executeService<IEnum[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}get-branch-choices/`,
    });
  }
}
